type ToastTone = 'info' | 'success' | 'warning' | 'danger';

/** How long a success toast stays; the kit's own default, written down here so the rule has one home. */
export const SUCCESS_TOAST_MS = 5000;
/** How long a switch or revoke notice stays. */
export const NOTICE_TOAST_MS = 4000;

/**
 * Toast options by tone: an error stays until the person dismisses it (0), a success goes after about 5 s, any other notice after 4 s.
 * The kit pauses on hover and announces as `alert` for errors by itself only if it implements it: it does not (see the MR).
 */
export function toastDuration(tone: ToastTone): number {
  if (tone === 'danger') return 0;
  return tone === 'success' ? SUCCESS_TOAST_MS : NOTICE_TOAST_MS;
}

const TOAST_SELECTOR = '[role="status"][class*="vn-toast-"]';

/**
 * Whether a click or focus landed on a toast. The toast region is a portal on <body>, outside every dialog, so for a dialog a
 * click on a toast's Dismiss is an interaction "outside" and closes it, discarding the form. Radix defers that check to the
 * `click` (a dialog sets `deferPointerDownOutside`), by which time the Dismiss has already removed the toast from the document:
 * a position test on the parents fails then (R5 B6). So the toast is told by what it is, the kit's `role=status` element
 * with a `vn-toast-` class, which still holds on a detached node. A page alert that happens to be a `status` has no such class.
 */
export function isToastTarget(target: EventTarget | null): boolean {
  return !!(target as Element | null)?.closest?.(TOAST_SELECTOR);
}

/** `onInteractOutside` for a dialog that holds a form: leaves it open when the interaction is with a toast. */
export function keepOpenOnToast(event: { target: EventTarget | null; preventDefault: () => void }): void {
  if (isToastTarget(event.target)) event.preventDefault();
}

type OutsideEvent = { target: EventTarget | null; preventDefault: () => void };

/** The `onInteractOutside` of the shell `DialogContent`: the caller's handler first, then the toast rule. */
export function composeInteractOutside<E extends OutsideEvent>(caller?: (event: E) => void): (event: E) => void {
  return (event) => {
    caller?.(event);
    keepOpenOnToast(event);
  };
}
